import { useMemo, useState } from "react";
import { LineChart as LineChartIcon } from "lucide-react";
import { PieChart } from "@mui/x-charts/PieChart";
import DropDownWithSearch from "../shared/DropDownWIthSearch";

const CandidateGaugePanel = ({
  title = "",
  jobRoles = {},        // job_roles object from API
  overallStats = {},    // total stats
  colors = {},          // color config
  onGaugeClick = () => {},
}) => {
  const [selectedJob, setSelectedJob] = useState("all");

  /* ---------------- Dropdown Data ---------------- */
  const dropdownData = useMemo(() => {
    const roles = Object.keys(jobRoles).map((role) => ({
      label: role,
      value: role,
    }));

    return [
      { label: "All", value: "all" },
      ...roles
    ];
  }, [jobRoles]);

  /* ---------------- Active data (overall or job-specific) ---------------- */
  const activeData = selectedJob === "all"
    ? overallStats
    : jobRoles[selectedJob];

  const completedValue = activeData?.completed ?? 0;
  const pendingValue = activeData?.pending ?? 0;
  const inProgressValue = activeData?.in_progress ?? 0;
  // const selectedValue = activeData?.selected ?? 0;
  // const rejectedValue = activeData?.rejected ?? 0;
  const totalValue = pendingValue + inProgressValue + completedValue;

  const pieData = [
    { value: pendingValue, color: colors.pending, label: "Scheduled" },
    { value: inProgressValue, color: colors.scheduled, label: "In Progress" },
    { value: completedValue, color: colors.completed, label: "Completed" },
    // { value: selectedValue, color: colors.selected || "#10B981", label: "Selected" },
    // { value: rejectedValue, color: colors.rejected || "#EF4444", label: "Rejected" },
  ].filter(item => item.value > 0);

  /* ---------------- Dummy Gauge Data ---------------- */
  const dummyPieData = [
    { value: 40, color: "#9CA3AF" },
    { value: 30, color: "#B0B8C4" },
    { value: 30, color: "#D1D5DB" },
  ];

  const chartPieData = totalValue === 0 ? dummyPieData : pieData;

  /* ---------------- Select handler ---------------- */
  const handleJobSelect = (item) => {
    if (!item || item.value === "all") {
      setSelectedJob("all");
    } else {
      setSelectedJob(item.value);
    }
  };

  return (
    <>
      <div className="w-full p-6 dark:bg-gradient-to-r from-[#232142]/10 to-[#7E64A3]/10 bg-bgSecondary rounded-xl flex flex-col text-gray-800 dark:text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LineChartIcon className="w-5 h-5 text-[#404040] dark:text-[#AEB9E1]" />
            <h2 className="text-[#404040] dark:text-[#AEB9E1] text-lg font-medium tracking-wide">{title}</h2>
          </div>
          <div className="w-64 relative">
            <DropDownWithSearch
              dropdownData={dropdownData}
              onSelect={handleJobSelect}
              placeholder="Select Job Role"
              selectedValue={{
                label: selectedJob === "all" ? "All" : selectedJob,
                value: selectedJob === "all" ? "all" : selectedJob,
              }}
              showClearButton={false}
            />

            {totalValue === 0 && (
              <div className="absolute inset-0 z-10 backdrop-blur-[2px] bg-white/20 dark:bg-gray-800/20 rounded-md"></div>
            )}
          </div>
        </div>

        {/* ================= Main Content ================= */}
        <div className="flex items-center relative">
          {totalValue === 0 && (
            <div className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none backdrop-blur-[2px]">
              <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm px-4 py-2 rounded-md border border-gray-200 dark:border-gray-700/50 shadow-sm">
                <span className="text-gray-500 dark:text-gray-400 text-sm font-medium">
                  No data to display
                </span>
              </div>
            </div>
          )}
          {/* -------- Gauge -------- */}
          <div className="w-1/2 flex justify-center relative mt-28 mx-10 h-[40px] items-center">
            {totalValue === 0 ? (
              <>
                {/* {totalValue === 0 && (
                                <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
                                    <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm px-4 py-2 rounded-md border border-gray-200 dark:border-gray-700/50 shadow-sm -translate-y-10">
                                    <span className="text-gray-500 dark:text-gray-400 text-sm font-medium">
                                        No data to display
                                    </span>
                                    </div>
                                </div>
                            )} */}

                <div className={totalValue === 0 ? "opacity-60 pointer-events-none" : ""}>
                  <PieChart
                    series={[
                      {
                        data: chartPieData,
                        innerRadius: 90,
                        outerRadius: 105,
                        paddingAngle: 0,
                        startAngle: -90,
                        endAngle: 90,
                        stroke: "none",
                        strokeWidth: 0,
                      },
                    ]}
                    width={210}
                    height={210}
                    margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
                    slotProps={{
  legend: { hidden: true },
  itemContent: undefined,  // keep tooltip default
}}
sx={{
  '& .MuiChartsLegend-root': { display: 'none' },  // force hide via CSS
  '& .MuiPieArc-root': {
    stroke: 'none',
    strokeWidth: 0,
  },
}}
                  />

                  <div className="absolute inset-0 flex flex-col items-center justify-center -translate-y-6 pointer-events-none">
                    <span className="text-2xl font-semibold dark:text-[#FFFFFF] text-[#404040]">
                      {totalValue}
                    </span>
                    <span className="text-xs text-[#808080] dark:text-[#7E89AC] tracking-wide">
                      Total Candidates
                    </span>
                  </div>
                </div>
              </>
            ) : (
              <>
                <PieChart
                  series={[
                    {
                      data: pieData,
                      innerRadius: 90,
                      outerRadius: 105,
                      paddingAngle: 0,
                      // cornerRadius: 5,
                      startAngle: -90,
                      endAngle: 90,
                      stroke: 'none',
                      strokeWidth: 0,
                    },
                  ]}
                  width={210}
                  height={210}
                  margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
                  onItemClick={(event, d) => {
                    const selectedItem = pieData[d.dataIndex];
                    if (selectedItem) {
                      onGaugeClick(selectedJob, selectedItem.label);
                    }
                  }}
                  slotProps={{
                    legend: { hidden: true },
                    itemContent: undefined, // keep tooltip default
                  }}
                  sx={{
                    '& .MuiChartsLegend-root': { display: 'none' }, // force hide via CSS
                    '& .MuiPieArc-root': {
                      stroke: 'none',
                      strokeWidth: 0,
                      cursor: 'pointer',
                    },
                  }}
                />
                <div className="absolute inset-0 flex flex-col items-center justify-center -translate-y-6 pointer-events-none">
                  <span
                    className="text-2xl font-semibold dark:text-[#FFFFFF] text-[#404040]"
                  >
                    {totalValue}
                  </span>
                  <span className="text-xs text-[#808080] dark:text-[#7E89AC] tracking-wide">
                    Total Candidates
                  </span>
                </div>
              </>
            )}
          </div>

          {/* -------- Stats -------- */}
          <div className="w-1/2 space-y-4">
            {[
              { label: "Scheduled", key: "pending", color: colors.pending },
              { label: "In Progress", key: "in_progress", color: colors.scheduled },
              { label: "Completed", key: "completed", color: colors.completed },
              // { label: "Selected", key: "selected", color: colors.selected || "#10B981" },
              // { label: "Rejected", key: "rejected", color: colors.rejected || "#EF4444" },
            ].map((item) => {
              const isDisabled = totalValue === 0;

              return (
                <div
                  key={item.key}
                  className={`flex items-center justify-between border-white/10 ${isDisabled ? "" : "cursor-pointer hover:bg-black/5 dark:hover:bg-white/5 rounded px-2 -mx-2 transition-colors"}`}
                  onClick={() => {
                    if (!isDisabled) {
                      onGaugeClick(selectedJob, item.label);
                    }
                  }}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{
                        backgroundColor: isDisabled ? "#9CA3AF" : item.color,
                      }}
                    />
                    <span
                      className={`text-sm ${isDisabled
                          ? "text-gray-400 dark:text-gray-500"
                          : "text-gray-800 dark:text-white"
                        }`}
                    >
                      {item.label}
                    </span>
                  </div>

                  <span
                    className={`font-medium ${isDisabled
                        ? "text-gray-400 dark:text-gray-500"
                        : "text-gray-800 dark:text-white"
                      }`}
                  >
                    {activeData?.[item.key] ?? 0}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};

export default CandidateGaugePanel;